import {useParams} from 'react-router-dom'
import cn from 'clsx'
import {parseISO} from 'date-fns'
import {Table, Space} from 'antd'

import styles from './ForecastHistory.module.scss'
import {User} from 'modules/users'
import {useOpen} from 'common/hooks'
import {
  Text,
  CentsMoney,
  Circle,
  Box,
  Button,
  Modal,
  UserAvatar,
  IconStat,
  TargetGoalIcon,
} from 'common/components'
import {useGetForecast} from 'common/api'
import {
  fixNumber,
  formatDate,
  isEstimatedForecastItem,
  totalNumberFromArray,
} from 'common/helpers'
import {ForecastItem, ForecastFormItem} from 'common/types'
import {ForecastItemForm, useGetProject} from '..'

interface ForecastHistoryProps {
  isRepayment: boolean
  item: ForecastItem
  number: number
}

interface EditForecastProps {
  projectUuid: string
  minDate: Date
  item: ForecastItem
  loanValue: number
}

interface SummaryRowProps {
  item: ForecastItem
  number: number
}

export default function ForecastHistory({
  isRepayment,
  item,
  number,
}: ForecastHistoryProps) {
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)

  const historyData: any = [...item.values].reverse().map((value) => ({
    ...value,
    key: value.createdAt,
  }))

  const estimated = isEstimatedForecastItem(item)
  const notClosed = project.status !== 'closed'

  const columns = [
    {
      dataIndex: 'createdAt',
      render: (value: string) => (
        <Text weight="demiBold" color="dimGrey">
          {formatDate(value, 'third')}
        </Text>
      ),
    },
    {
      dataIndex: 'createdBy',
      render: (value: User) => (
        <Space size={16}>
          <UserAvatar src={value.photo} size={26} alt={value.firstName} />
          <span>{value.firstName}</span>
        </Space>
      ),
    },
    {
      dataIndex: 'date',
      render: (value: string) => (
        <Text align="right" size="xs">
          {formatDate(value, 'fifth')}
        </Text>
      ),
    },
    {
      dataIndex: 'amount',
      render: (value: number, record: any) => {
        const amountJSX = (
          <CentsMoney as="span" value={value} weight="demiBold" />
        )
        return (
          <Box align="right">
            <Space size={16}>
              {item.valueForRating && item.valueForRating.createdAt === record.createdAt && (
                <Circle border color="turquoize" />
              )}
              <Text weight="demiBold" color="turquoizeBright">
                {isRepayment ? <>({amountJSX})</> : amountJSX}
              </Text>
            </Space>
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <Table
        className={cn(styles.table, estimated && styles.table_estimated)}
        showHeader={false}
        pagination={false}
        columns={columns}
        dataSource={historyData}
        summary={
          estimated
            ? undefined
            : () => <SummaryRow item={item} number={number} />
        }
      />
      {estimated
        ? notClosed && (
            <EditForecast
              projectUuid={params.uuid}
              minDate={parseISO(project.openDate)}
              item={item}
              loanValue={project.totalAmount}
            />
          )
        : !item.isFake &&
          item.rating && (
            <IconStat
              row
              icon={<TargetGoalIcon />}
              iconColor="turquoize"
              value={
                <>
                  {fixNumber(item.rating.points)}{' '}
                  <Text as="span" size="lg" color="grey">
                    {item.rating.variance}% variance
                  </Text>
                </>
              }
              label={`Points for the ${item.type} forecast`}
            />
          )}
    </>
  )
}

const SummaryRow = ({item, number}: SummaryRowProps) => {
  const {transactions} = item
  const firstTransaction = transactions?.length ? transactions[0] : null

  return (
    <Summary.Row>
      <Summary.Cell index={0}>
        <Text uppercaseFirstLetter weight="demiBold" color="dimGrey">
          {firstTransaction ? firstTransaction.type : item.type} {number}
        </Text>
      </Summary.Cell>
      <Summary.Cell index={1} colSpan={2}>
        <Text align="right" weight="demiBold" color="dimGrey">
          {formatDate(
            firstTransaction ? firstTransaction.date : item.value.date,
            'fifth'
          )}
        </Text>
      </Summary.Cell>
      <Summary.Cell index={2} align="right">
        <Space size={16}>
          <Circle border color="darkGrey" />
          <CentsMoney
            value={
              firstTransaction ? firstTransaction.amount : item.value.amount
            }
            align="right"
            weight="demiBold"
            color="dimGrey"
          />
        </Space>
      </Summary.Cell>
    </Summary.Row>
  )
}

const EditForecast = ({
  projectUuid,
  minDate,
  item,
  loanValue,
}: EditForecastProps) => {
  const {data: forecast} = useGetForecast(projectUuid)
  const [isOpen, handleOpen, handleClose] = useOpen()

  const forecastItem = {
    uuid: item.uuid,
    type: item.type,
    date: parseISO(item.value.date),
    amount: item.transactions?.length
      ? totalNumberFromArray(item.transactions, 'amount')
      : item.value.amount,
    transaction: Boolean(item.transactions?.length),
  }

  const currentForecast: ForecastFormItem[] = forecast.map((item) => ({
    uuid: item.uuid,
    type: item.type,
    date: parseISO(item.value.date),
    amount: item.transactions?.length
      ? totalNumberFromArray(item.transactions, 'amount')
      : item.value.amount,
    transactions: Boolean(item.transactions?.length || item.isFake),
  }))

  return (
    <>
      <Box align="right">
        <Button onClick={handleOpen}>Edit forecast</Button>
      </Box>
      <Modal
        title="Edit forecast item"
        width={486}
        open={isOpen}
        onCancel={handleClose}
      >
        <ForecastItemForm
          projectUuid={projectUuid}
          minDate={minDate}
          forecastItem={forecastItem}
          loanValue={loanValue}
          forecast={currentForecast}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const {Summary} = Table
